import React, {useState} from 'react';

import * as ArchivePurposesUtil from 'archive/ArchivePurposesUtil';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import * as Context from 'Context';
import * as AssayRecordDAO from 'results/assays/AssayRecordDAO';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {refreshChips} from 'ui-library/chips/useSmartChipQuery';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';

export const LARGE_FILES_FOOTER_TEXT = 'For larger files, this action may take a few minutes';

export interface LabAutoResultsArchiveModalProps {
  onRequestClose: () => void;
  fileName: string;
  automationOutputFileId: string;
  updateAutomationOutputFile: (automationOutputFile: AutomationOutputFileGraphQL) => void;
  analyticsMetadata: AnalyticsMetadataForContext;
}

export function LabAutoResultsArchiveModal(props: LabAutoResultsArchiveModalProps): JSX.Element {
  const validPurposes = ArchivePurposesUtil.getValidArchivePurposes([props.automationOutputFileId]);
  const [archivePurpose, setArchivePurpose] = useState(validPurposes[0]);
  const optionDatas: Array<OptionData> = validPurposes.map((validPurpose) => ({
    name: validPurpose,
    value: validPurpose,
  }));
  const changeArchivePurpose = (newArchivePurpose: string): void => {
    setArchivePurpose(newArchivePurpose);
  };
  return (
    <Modal
      medium={true}
      closeableOnBackgroundClick={true}
      closeableOnEscapeKey={true}
      bodyPadded={false}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={async () => {
        await Context.usingAsync(
          {
            analyticsMetadata: props.analyticsMetadata,
          },
          null,
          async () => {
            const response = await withLoadingScreen(
              AssayRecordDAO.archiveAutomationOutputFile(props.automationOutputFileId, archivePurpose),
              'Archiving file...'
            );
            const chipIdsToRefresh: Array<string> = [];
            if (response.automationOutputFile.datasetId != null) {
              chipIdsToRefresh.push(response.automationOutputFile.datasetId);
            }
            if (response.automationOutputFile.analysisId != null) {
              chipIdsToRefresh.push(response.automationOutputFile.analysisId);
            }
            refreshChips(chipIdsToRefresh);
            props.updateAutomationOutputFile(response.automationOutputFile);
          }
        );
        props.onRequestClose();
      }}
      title="Archive items"
      dataTestAttrs={{component: 'LabAutoResultsArchiveModal', element: 'root'}}
      buttons={[
        {
          text: 'Archive',
          style: ButtonStyle.NEGATIVE,
          submit: true,
          disabled: false,
        },
      ]}
      footer={<span className="u-faded">{LARGE_FILES_FOOTER_TEXT}</span>}
      footerPullRight={true}
    >
      <div className="modal-body">
        <Row>
          <Col md={12}>
            <p>
              <b>{`${props.fileName} `}</b> will be archived
            </p>
          </Col>
        </Row>
        <div>
          <Row>
            <Col md={12}>
              <FieldLabel label="Mark as" />
            </Col>
          </Row>
          <Row className="u-vertical-center-items">
            <Col md={12}>
              <SelectDropdown
                displayName={archivePurpose}
                menu={
                  <SearchableMenu
                    onSelect={changeArchivePurpose}
                    optionDatas={optionDatas}
                    selectedValue={archivePurpose}
                  />
                }
              />
            </Col>
          </Row>
        </div>
      </div>
    </Modal>
  );
}

export default LabAutoResultsArchiveModal;
